import React, {useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import InventoryStructuredTableMode, {
  InventoryTableInsertionTableType,
} from 'notebook/integrations/samples/InventoryStructuredTableMode';
import InventoryTableInsertionModeToggle, {
  InventoryTableInsertionMode,
} from 'notebook/structured-tables/components/InventoryTableInsertionModeToggle';
import {
  InventoryTableModalTrackingParams,
  trackModalClose,
  trackModalOpen,
  trackModalSubmit,
} from 'notebook/structured-tables/components/InventoryTableModalTracking';
import {InventoryTablePreview} from 'notebook/structured-tables/components/InventoryTablePreview';
import {
  TransferStructuredTableInsertForm,
  TransferStructuredTableInsertionConfiguration,
} from 'notebook/structured-tables/components/modals/TransferTableInsertModal';
import UpdateContainersStructuredTableInsertForm, {
  UpdateContainersStructuredTableInsertionConfiguration,
} from 'notebook/structured-tables/components/UpdateContainersStructuredTableInsertForm';
import useSelectorsById, {selectorsAreLoaded} from 'notebook/structured-tables/hooks/useSelectorsById';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {
  LegacyTransferTableInsertForm,
  LegacyTransferTableInsertionConfiguration,
} from 'notebook/transfer-tables/LegacyTransferTableInsertModal';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import Modal from 'ui-library/modals/Modal';

export interface InventoryTableInsertModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (
    insertionConfig: Exclude<InventoryStructuredTableInsertionConfiguration, 'INVALID'>,
    selectorsById: {[selectorId: string]: SchemaFieldSelector}
  ) => void;
  trackingParams?: InventoryTableModalTrackingParams;
  structuredTableInsertionContext: StructuredTableInsertionContextValue;
}

type InventoryStructuredTableInsertionConfiguration =
  | TransferStructuredTableInsertionConfiguration
  | LegacyTransferTableInsertionConfiguration
  | UpdateContainersStructuredTableInsertionConfiguration;

function getTablePreview(
  tableInsertionConfig: InventoryStructuredTableInsertionConfiguration,
  selectorsById: {[selectorId: string]: SchemaFieldSelector},
  structuredTableInsertionContext: StructuredTableInsertionContextValue
): JSX.Element | null {
  if (tableInsertionConfig === 'INVALID') {
    return null;
  }
  if (tableInsertionConfig.tableType === InventoryTableInsertionTableType.UPDATE_CONTAINERS) {
    const tableNameBase = tableInsertionConfig.storableSchema
      ? tableInsertionConfig.storableSchema.name
      : (tableInsertionConfig.metadata.storableSchemaType as string) ===
          (ModelTypeConstants.PLATE_SCHEMA as string)
        ? 'Any plate'
        : 'Any container';
    const schemaWithEmptyColumnGroup = [];
    if (tableInsertionConfig.storableSchema) {
      schemaWithEmptyColumnGroup.push({...tableInsertionConfig.storableSchema, columnGroup: null});
    }
    if (tableInsertionConfig.plateWellSchema) {
      schemaWithEmptyColumnGroup.push({...tableInsertionConfig.plateWellSchema, columnGroup: null});
    }
    return (
      <InventoryTablePreview
        inventoryTableKind={StructuredTableKind.INVENTORY}
        name={tableNameBase}
        schemas={schemaWithEmptyColumnGroup}
        cid="123"
        selectorsById={selectorsById}
        metadata={tableInsertionConfig.metadata}
        structuredTableInsertionContext={structuredTableInsertionContext}
      />
    );
  } else if (tableInsertionConfig.tableType === InventoryTableInsertionTableType.TRANSFER) {
    let name: string | null = null;
    const schemaWithEmptyColumnGroup = [
      {
        ...tableInsertionConfig.destinationContainerSchema,
        columnGroup: null,
      },
    ];
    if (tableInsertionConfig.destinationPlateSchema) {
      schemaWithEmptyColumnGroup.push({...tableInsertionConfig.destinationPlateSchema, columnGroup: null});
      name = tableInsertionConfig.destinationPlateSchema.name;
    }
    return (
      <InventoryTablePreview
        inventoryTableKind={StructuredTableKind.TRANSFER}
        name={name}
        schemas={schemaWithEmptyColumnGroup}
        cid="123"
        selectorsById={selectorsById}
        metadata={tableInsertionConfig.metadata}
        structuredTableInsertionContext={structuredTableInsertionContext}
      />
    );
  }
  return null;
}

function extractSchemaIdFromInsertionConfig(
  insertionConfig: InventoryStructuredTableInsertionConfiguration
): string | null {
  assert(insertionConfig !== 'INVALID', 'Expecting insertionConfig to be valid');
  switch (insertionConfig.tableType) {
    case InventoryTableInsertionTableType.LEGACY_TRANSFER:
      return insertionConfig.containerSchema.id;
    case InventoryTableInsertionTableType.TRANSFER:
      return insertionConfig.destinationPlateSchema?.id ?? insertionConfig.destinationContainerSchema.id;
    case InventoryTableInsertionTableType.UPDATE_CONTAINERS:
      return insertionConfig.storableSchema?.id ?? null;
    default:
      return null;
  }
}

/**
 * The inventory table insert modal can insert three different kinds of tables:
 * - The legacy transfer table, which is specified by a LegacyTransferTableInsertionConfiguration configured
 *   using LegacyTransferTableInsertForm.
 * - The transfer structured table, which is specified by a TransferStructuredTableInsertionConfiguration
 *   configured using a TransferStructuredTableInsertForm.
 * - The update container attributes table, specified by a UpdateContainersStructuredTableInsertionConfiguration
 *   configured using an UpdateContainersStructuredTableInsertForm.
 */
export default function InventoryTableInsertModal({
  onRequestClose,
  onRequestSubmit,
  trackingParams,
  structuredTableInsertionContext,
}: InventoryTableInsertModalProps): JSX.Element {
  useOnMount(() => {
    if (trackingParams) {
      trackModalOpen(trackingParams);
    }
  });
  const includeNewTransferOptions = BenchlingConfig.ELN_ENABLE_INSERT_NEW_TRANSFER_TABLE;
  const modeOptions = includeNewTransferOptions
    ? [
        InventoryStructuredTableMode.CREATE,
        InventoryStructuredTableMode.FILL,
        InventoryStructuredTableMode.UPDATE_CONTAINERS,
      ]
    : [InventoryStructuredTableMode.LEGACY_TRANSFER, InventoryStructuredTableMode.UPDATE_CONTAINERS];
  const [mode, setMode] = useState<InventoryTableInsertionMode>(modeOptions[0]);
  const [structuredTableInsertionConfig, setStructuredTableInsertionConfig] =
    useState<InventoryStructuredTableInsertionConfiguration>('INVALID');
  const selectorsById = useSelectorsById({includeArchivedSelectors: false});

  const handleOnRequestClose = (): void => {
    if (trackingParams) {
      trackModalClose({
        ...trackingParams,
        tableActionParams: {
          tableActionModeChosen: mode as InventoryStructuredTableMode,
          tableActionModesShown: modeOptions,
        },
      });
    }
    onRequestClose();
  };

  const handleOnRequestSubmit = (): void => {
    assert(
      structuredTableInsertionConfig !== 'INVALID' && selectorsAreLoaded(selectorsById),
      'Expecting structuredTableInsertionConfig to be valid and selectorsById to be loaded when submit button is enabled'
    );
    const schemaId = extractSchemaIdFromInsertionConfig(structuredTableInsertionConfig);
    if (trackingParams) {
      trackModalSubmit({
        ...trackingParams,
        tableActionParams: {
          tableActionModeChosen: mode as InventoryStructuredTableMode,
          tableActionModesShown: modeOptions,
        },
        submitParams: {
          schemaSelected: schemaId != null,
          schemaId,
          tableTypeAdded:
            mode === InventoryStructuredTableMode.UPDATE_CONTAINERS
              ? 'inventory'
              : includeNewTransferOptions
                ? 'transfer'
                : 'legacy_transfer',
        },
      });
    }
    onRequestSubmit(structuredTableInsertionConfig, selectorsById);
  };

  return (
    <Modal
      title="Insert inventory table"
      onRequestClose={handleOnRequestClose}
      onRequestSubmit={handleOnRequestSubmit}
      buttons={[
        {
          text: 'Insert',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: structuredTableInsertionConfig === 'INVALID' || !selectorsAreLoaded(selectorsById),
        },
      ]}
      className="mediocre" // TableEditable's CSS is namespaced under mediocre
    >
      <InventoryTableInsertionModeToggle
        currentMode={mode}
        setMode={(newMode: InventoryTableInsertionMode) => {
          setMode(newMode);
          if (
            newMode === InventoryStructuredTableMode.UPDATE_CONTAINERS ||
            newMode === InventoryStructuredTableMode.LEGACY_TRANSFER
          ) {
            setStructuredTableInsertionConfig('INVALID');
          }
        }}
        includeInsertionModes={modeOptions}
      />
      {(mode === InventoryStructuredTableMode.CREATE || mode === InventoryStructuredTableMode.FILL) && (
        <TransferStructuredTableInsertForm
          mode={mode as InventoryStructuredTableMode}
          onChange={setStructuredTableInsertionConfig}
          structuredTableInsertionContext={structuredTableInsertionContext}
        />
      )}
      {mode === InventoryStructuredTableMode.LEGACY_TRANSFER && (
        <LegacyTransferTableInsertForm onChange={setStructuredTableInsertionConfig} />
      )}
      {mode === InventoryStructuredTableMode.UPDATE_CONTAINERS && (
        <UpdateContainersStructuredTableInsertForm onChange={setStructuredTableInsertionConfig} />
      )}
      {selectorsAreLoaded(selectorsById) &&
        getTablePreview(structuredTableInsertionConfig, selectorsById, structuredTableInsertionContext)}
    </Modal>
  );
}
